"use client"

import { ErrorBoundary as SentryErrorBoundary } from "@sentry/nextjs"
import React, { Component } from "react"
import ErrorPage from "components/ErrorPage"
import analytics from "utils/analytics"

interface ErrorBoundaryWrapperProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  useSentry?: boolean
  onError?: (error: Error, info: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  error: Error | null
}

const initialState: ErrorBoundaryState = { error: null }

class ErrorBoundary extends Component<ErrorBoundaryWrapperProps, ErrorBoundaryState> {
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  constructor(props: ErrorBoundaryWrapperProps) {
    super(props)
    this.state = initialState
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const { onError } = this.props

    // Track error in analytics
    analytics?.log({
      log_type: "error_boundary_catch",
      custom: {
        error_name: error.name,
        error_message: error.message,
        error_stack: error.stack,
        component_stack: info.componentStack,
        error_digest: info.digest,
        error_boundary_type: "custom",
      },
    })

    if (onError) onError(error, info)
  }

  render() {
    const { error } = this.state
    const { fallback, children } = this.props

    if (error !== null) {
      if (React.isValidElement(fallback)) {
        return fallback
      }
      return <ErrorPage />
    }
    return children
  }
}

const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({ useSentry = true, children, ...props }) => {
  const handleSentryError = (error: unknown, componentStack: string | undefined, eventId: string) => {
    // Track error in analytics for Sentry boundary
    const errorObj = error instanceof Error ? error : new Error(String(error))

    analytics?.log({
      log_type: "error_boundary_catch",
      custom: {
        error_name: errorObj.name,
        error_message: errorObj.message,
        error_cause: errorObj.cause,
        error_stack: errorObj.stack,
        component_stack: componentStack,
        error_boundary_type: "sentry",
        sentry_event_id: eventId,
      },
    })

    // Call original onError if provided
    if (props.onError && errorObj) {
      const mockErrorInfo: React.ErrorInfo = {
        componentStack: componentStack || "",
        digest: undefined,
      }
      props.onError(errorObj, mockErrorInfo)
    }
  }

  if (useSentry) {
    return (
      <SentryErrorBoundary
        fallback={React.isValidElement(props.fallback) ? props.fallback : <ErrorPage />}
        onError={handleSentryError}
      >
        {children}
      </SentryErrorBoundary>
    )
  }

  return <ErrorBoundary {...props}>{children}</ErrorBoundary>
}

export default ErrorBoundaryWrapper
